import type { Locale } from '@xdocs/i18n';

/**
 * STOP-GAP local catalog for the `adminAudit:` scope (F59), following the
 * `shares:`/`search:` precedent — see `apps/web/src/routes/shares/shares.i18n.ts`.
 * NOT a second i18n runtime (E18/§13.3 ban); `packages/i18n/**` is outside
 * this task's ownership.
 *
 * OPEN ISSUE: fold into `packages/i18n/src/catalogs/{en,ro}/admin.json`.
 */
const en = {
  'page.title': 'Audit log',
  'page.description': 'Read-only. Every sensitive mutation across the platform.', // mockup-48
  'filter.action.label': 'Action',
  'filter.action.all': 'All actions',
  'filter.actor.label': 'Actor user ID',
  'filter.actor.placeholder': 'Paste a user ID…',
  'filter.target.label': 'Target ID',
  'filter.target.placeholder': 'Paste a target ID…',
  'filter.from.label': 'From',
  'filter.to.label': 'To',
  'filter.impersonationOnly.label': 'View-as sessions only',
  'filter.apply': 'Apply filters',
  'filter.reset': 'Reset',
  'list.column.timestamp': 'Timestamp',
  'list.column.actor': 'Actor',
  'list.column.action': 'Action',
  'list.column.target': 'Target',
  'list.column.reason': 'Reason',
  'list.column.ip': 'IP',
  'reason.none': '—',
  'list.empty.title': 'No audit events yet',
  'list.empty.description': 'Admin actions and view-as sessions will show up here as they happen.',
  'list.error.title': "Couldn't load the audit log.",
  'list.noResults.title': 'No events match the current filters',
  'list.searchLabel': 'Search audit events',
  'list.searchPlaceholder': 'Search…',
  'detail.title': 'Audit event',
  'detail.field.actor': 'Actor',
  'detail.field.action': 'Action',
  'detail.field.target': 'Target',
  'detail.field.impersonated': 'Impersonated user',
  'detail.field.ip': 'IP address',
  'detail.field.userAgent': 'User agent',
  'detail.field.timestamp': 'Timestamp',
  'detail.field.metadata': 'Metadata (before/after + reason)',
  'detail.metadata.none': 'No metadata recorded.',
  'target.deleted': 'no longer available',
  'action.user.tier_change': 'Tier changed',
  'action.user.credit_adjust': 'Credits adjusted',
  'action.user.role_change': 'Role changed',
  'action.user.suspend': 'User suspended/unsuspended',
  'action.user.force_logout': 'Force-logout',
  'action.user.resend_verify': 'Verification email resent',
  'action.promo.create': 'Promo code created',
  'action.promo.update': 'Promo code updated',
  'action.promo.deactivate': 'Promo code deactivated',
  'action.asset.unpublish': 'Asset unpublished',
  'action.asset.remove': 'Asset removed',
  'action.comment.remove': 'Comment removed',
  'action.landing.publish': 'Landing block published',
  'action.legal.publish': 'Legal document published',
  'action.contact.handle': 'Contact submission handled',
  'action.impersonation.start': 'View-as started',
  'action.impersonation.end': 'View-as ended',
  'action.document.create': 'Document created',
  'action.share.publish': 'Share published',
  'action.comment.create': 'Comment created',
  'action.user.delete_account': 'Account deleted (Danger Zone)',
  'action.user.export_data': 'Data export requested (Danger Zone)',
} as const;

const ro = {
  'page.title': 'Jurnal de audit',
  'page.description': 'Doar în citire. Fiecare mutație sensibilă de pe platformă.', // mockup-48
  'filter.action.label': 'Acțiune',
  'filter.action.all': 'Toate acțiunile',
  'filter.actor.label': 'ID utilizator (actor)',
  'filter.actor.placeholder': 'Lipește un ID de utilizator…',
  'filter.target.label': 'ID țintă',
  'filter.target.placeholder': 'Lipește un ID țintă…',
  'filter.from.label': 'De la',
  'filter.to.label': 'Până la',
  'filter.impersonationOnly.label': 'Doar sesiuni „vezi ca”',
  'filter.apply': 'Aplică filtrele',
  'filter.reset': 'Resetează',
  'list.column.timestamp': 'Marcaj de timp',
  'list.column.actor': 'Actor',
  'list.column.action': 'Acțiune',
  'list.column.target': 'Țintă',
  'list.column.reason': 'Motiv',
  'list.column.ip': 'IP',
  'reason.none': '—',
  'list.empty.title': 'Niciun eveniment de audit încă',
  'list.empty.description': 'Acțiunile admin și sesiunile „vezi ca” vor apărea aici pe măsură ce au loc.',
  'list.error.title': 'Nu am putut încărca jurnalul de audit.',
  'list.noResults.title': 'Niciun eveniment nu corespunde filtrelor curente',
  'list.searchLabel': 'Caută evenimente de audit',
  'list.searchPlaceholder': 'Caută…',
  'detail.title': 'Eveniment de audit',
  'detail.field.actor': 'Actor',
  'detail.field.action': 'Acțiune',
  'detail.field.target': 'Țintă',
  'detail.field.impersonated': 'Utilizator vizualizat',
  'detail.field.ip': 'Adresă IP',
  'detail.field.userAgent': 'User agent',
  'detail.field.timestamp': 'Marcaj de timp',
  'detail.field.metadata': 'Metadate (înainte/după + motiv)',
  'detail.metadata.none': 'Nicio metadată înregistrată.',
  'target.deleted': 'nu mai este disponibil',
  'action.user.tier_change': 'Nivel schimbat',
  'action.user.credit_adjust': 'Credite ajustate',
  'action.user.role_change': 'Rol schimbat',
  'action.user.suspend': 'Utilizator suspendat/repus',
  'action.user.force_logout': 'Deconectare forțată',
  'action.user.resend_verify': 'Email de verificare retrimis',
  'action.promo.create': 'Cod promoțional creat',
  'action.promo.update': 'Cod promoțional actualizat',
  'action.promo.deactivate': 'Cod promoțional dezactivat',
  'action.asset.unpublish': 'Conținut retras',
  'action.asset.remove': 'Conținut eliminat',
  'action.comment.remove': 'Comentariu eliminat',
  'action.landing.publish': 'Bloc landing publicat',
  'action.legal.publish': 'Document legal publicat',
  'action.contact.handle': 'Mesaj de contact procesat',
  'action.impersonation.start': 'Sesiune „vezi ca” începută',
  'action.impersonation.end': 'Sesiune „vezi ca” încheiată',
  'action.document.create': 'Document creat',
  'action.share.publish': 'Distribuire publicată',
  'action.comment.create': 'Comentariu creat',
  'action.user.delete_account': 'Cont șters (Zonă periculoasă)',
  'action.user.export_data': 'Export de date solicitat (Zonă periculoasă)',
} as const;

export const ADMIN_AUDIT_CATALOG: Record<Locale, Record<AdminAuditCatalogKey, string>> = { en, ro };
export type AdminAuditCatalogKey = keyof typeof en;
